import { ErrorBoundary, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box, FlatList, Skeleton } from 'native-base';
import React, { ReactNode } from 'react';
import { StyleSheet } from 'react-native';

import { DirectTreasuryDetailsItemProps } from '../..';
import { EmptyDetails } from '../EmptyDetails';
import { InfoItem } from '../InfoItem';

import { parseDetailsItemData, parseExtraDetailsItemData } from './constants';

interface MoreDetailsDesktopProps {
  details: DirectTreasuryDetailsItemProps[];
  isLoading: boolean;
  isError: boolean;
  refresher: () => void;
}

export const ItemSeparatorComponent = () => (
  <Box bg={'neutral.lightest'} h={0.8} mt={Spacing['inline.xxxs']} />
);

const style = StyleSheet.create({
  columnWrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },
  gap: {
    rowGap: 16,
  },
  detailsContainer: {
    backgroundColor: Color.background,
    borderRadius: 8,
    padding: 16,
    justifyContent: 'space-between',
  },
});

const TabletWrapperItem: React.FC<{ children: ReactNode; isLastItem: boolean }> = ({
  children,
  isLastItem,
}) => <Box flex={isLastItem ? 2 : 1}>{children}</Box>;

export const MoreDetailsDesktop: React.FC<MoreDetailsDesktopProps> = ({
  details,
  isLoading,
  isError,
  refresher,
}) => {
  const numColumnsDetails = useBreakpointValue({ base: 5, lg: 9 });
  const numColumnsExtraDetails = useBreakpointValue({ base: 4, lg: 8 });

  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const WrapperItem = useBreakpointValue<React.FC<any>>({
    base: React.Fragment,
    lg: React.Fragment,
    sm: TabletWrapperItem,
  });

  if (isLoading) {
    return (
      <VStack borderTopWidth={1} borderColor={'neutral.lightest'} space={Spacing['inline.xxxs']}>
        <FlatList
          data={Array.from({ length: 9 })}
          style={[style.detailsContainer, style.gap]}
          key={`${numColumnsDetails}-loading`}
          numColumns={numColumnsDetails}
          showsVerticalScrollIndicator={false}
          keyExtractor={(_, index) => `${index.toString()}-loading-details`}
          renderItem={({ index }) => (
            <WrapperItem isLastItem={index === 9 - 1}>
              <VStack flex={1} space={Spacing['inline.nano']} maxWidth={'180px'}>
                <Skeleton
                  w="80%"
                  h={Typography['fontsize.md']}
                  borderRadius={BoxRadius.sm}
                  endColor={Color['brand.primary.lightest']}
                />
                <Skeleton
                  w="80%"
                  h={Typography['fontsize.md']}
                  borderRadius={BoxRadius.sm}
                  endColor={Color['brand.primary.lightest']}
                />
              </VStack>
            </WrapperItem>
          )}
        />
        <FlatList
          key={`${numColumnsExtraDetails}-loading`}
          data={Array.from({ length: 8 })}
          numColumns={numColumnsExtraDetails}
          style={style.gap}
          showsVerticalScrollIndicator={false}
          keyExtractor={(_, index) => `${index.toString()}-loading-extra-details`}
          {...(numColumnsExtraDetails !== 1 && { columnWrapperStyle: style.columnWrapper })}
          renderItem={() => (
            <Box minW={'120px'}>
              <VStack flex={0.7} space={Spacing['inline.nano']}>
                <Skeleton
                  w="100%"
                  h={Typography['fontsize.md']}
                  borderRadius={BoxRadius.sm}
                  endColor={Color['brand.primary.lightest']}
                />
                <Skeleton
                  w="100%"
                  h={Typography['fontsize.md']}
                  borderRadius={BoxRadius.sm}
                  endColor={Color['brand.primary.lightest']}
                />
              </VStack>
            </Box>
          )}
        />
      </VStack>
    );
  }

  if (!isLoading && isError) {
    return <ErrorBoundary onRefresh={refresher} />;
  }

  return (
    <VStack borderTopWidth={1} borderColor={'neutral.lightest'}>
      <FlatList
        data={details}
        key={numColumnsDetails}
        showsVerticalScrollIndicator={false}
        ItemSeparatorComponent={ItemSeparatorComponent}
        ListEmptyComponent={EmptyDetails}
        keyExtractor={(item) => item.titleId}
        renderItem={({ item }) => {
          const detailsItems = parseDetailsItemData(item);
          const extraDetailsItems = parseExtraDetailsItemData(item);

          return (
            <VStack space={Spacing['inline.xxxs']} mt={Spacing['stack.xxxs']}>
              <FlatList
                data={detailsItems}
                style={[style.detailsContainer, style.gap]}
                key={numColumnsDetails}
                numColumns={numColumnsDetails}
                showsVerticalScrollIndicator={false}
                keyExtractor={(detailKey) => detailKey.label}
                {...(isDesktop ? { columnWrapperStyle: style.columnWrapper } : {})}
                renderItem={({ item: DetailItem, index }) => (
                  <WrapperItem isLastItem={index === detailsItems.length - 1}>
                    <InfoItem {...DetailItem} />
                  </WrapperItem>
                )}
              />
              <FlatList
                key={numColumnsExtraDetails}
                data={extraDetailsItems}
                numColumns={numColumnsExtraDetails}
                style={style.gap}
                showsVerticalScrollIndicator={false}
                keyExtractor={(extraDetail) => extraDetail.label}
                {...(numColumnsExtraDetails !== 1 && { columnWrapperStyle: style.columnWrapper })}
                renderItem={({ item: ExtraDetailItem }) => (
                  <Box minW={'111px'}>
                    <InfoItem {...ExtraDetailItem} />
                  </Box>
                )}
              />
            </VStack>
          );
        }}
      />
    </VStack>
  );
};
